import { useState } from 'react';
import { Trans } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useJourneyLocale } from '../i18n/journey';
import { clearJournalDrafts } from '../state/journalDraft';
import { useUser } from '../state/UserContext';

/** Always accepted, whatever the UI language, so the check never depends on typing a script the
 * person's keyboard can't produce. */
const CONFIRM_WORD_EN = 'DELETE';

/** Self-contained account deletion (review fix 2026-09-10, finding 13): DELETE /api/me removes
 * the journal, profile/progress/routine and the sign-in account, then this signs out. Requires
 * typing a confirmation word -- there is no undo, because nothing is kept to restore from. */
export function AccountDeletion() {
  const { me } = useUser();
  const { logout } = useAuth();
  const { t } = useJourneyLocale();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The localized confirmation word (e.g. "ELIMINAR"); the English one is accepted too.
  const confirmWord = t('account.confirmWord');
  const typed = confirmText.trim();
  const confirmed = typed === confirmWord || typed === CONFIRM_WORD_EN;

  const handleDelete = async () => {
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteAccount();
      clearJournalDrafts();
      try {
        localStorage.removeItem('omnist.welcomeShownDate');
      } catch {
        // ignore
      }
      logout();
    } catch (e) {
      // A 502 means the data is already gone but the sign-in account wasn't removed; repeating
      // the request is safe and retries just that part.
      setError(e instanceof Error ? e.message : t('account.deleteFailed'));
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="btn btn-danger-outline" onClick={() => setOpen(true)}>
        {t('account.open')}
      </button>
    );
  }

  return (
    <div className="account-delete-panel" role="group" aria-labelledby="account-delete-heading">
      <h3 id="account-delete-heading" className="account-delete-heading">{t('account.heading')}</h3>
      <p>{t('account.body')}</p>
      {me?.tier === 'premium' && (
        <p className="account-delete-warning">
          <Trans i18nKey="account.premiumWarning" components={{ strong: <strong /> }} />
        </p>
      )}
      <label className="account-delete-label" htmlFor="account-delete-confirm">
        {t('account.confirmLabel', { word: confirmWord })}
      </label>
      <input
        id="account-delete-confirm"
        className="text-input"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        autoComplete="off"
        spellCheck={false}
      />
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div className="journal-entry-actions">
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleDelete}
          disabled={busy || !confirmed}
        >
          {busy ? t('account.deleting') : t('account.deleteButton')}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            setOpen(false);
            setConfirmText('');
            setError(null);
          }}
          disabled={busy}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
